<template>
  <Dialog :open="isOpen" @update:open="handleClose">
    <DialogContent class="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <MessageSquareWarning class="w-5 h-5 text-red-500" />
          Report Fellow Issue
        </DialogTitle>
        <DialogDescription>
          Please provide details about the issue you'd like to report. All reports are confidential and will be reviewed by our team.
        </DialogDescription>
      </DialogHeader>

      <form @submit.prevent="handleSubmit" class="space-y-4">
        <!-- Fellow Selection -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">
            Fellow to Report <span class="text-red-500">*</span>
          </label>
          <Popover v-model:open="open">
            <PopoverTrigger as-child>
              <Button
                variant="outline"
                role="combobox"
                :aria-expanded="open"
                class="w-full justify-between"
                :disabled="isLoading"
              >
                {{ selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name} (@${selectedUser.username})` : 'Select a fellow...' }}
                <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-full p-0">
              <Command v-model="form.fellow_username">
                <CommandInput placeholder="Search fellows..." />
                <CommandEmpty>No fellows found.</CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      v-for="user in filteredUsers"
                      :key="user.username"
                      :value="user.username"
                      @select="handleUserSelection(user)"
                    >
                      <Check
                        :class="cn(
                          'h-4 w-4',
                          form.fellow_username === user.username ? 'opacity-100' : 'opacity-0',
                        )"
                      />
                      {{ user.first_name }} {{ user.last_name }} (@{{ user.username }})
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div v-if="errors.fellow_username" class="text-sm text-red-600">
            {{ errors.fellow_username }}
          </div>
        </div>

        <!-- Reason Selection -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">
            Reason <span class="text-red-500">*</span>
          </label>
          <Select v-model="form.reason" :disabled="isLoading">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select a reason..." />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="UNPROFESSIONAL">Unprofessional conduct</SelectItem>
                <SelectItem value="COMMUNICATION">Poor communication</SelectItem>
                <SelectItem value="ATTENDANCE">Attendance issues</SelectItem>
                <SelectItem value="PERFORMANCE">Performance concerns</SelectItem>
                <SelectItem value="CODE_OF_CONDUCT">Code of conduct violation</SelectItem>
                <SelectItem value="HARASSMENT">Harassment or inappropriate behavior</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <div v-if="errors.reason" class="text-sm text-red-600">
            {{ errors.reason }}
          </div>
        </div>

        <!-- Description -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">
            Description <span class="text-red-500">*</span>
          </label>
          <Textarea
            v-model="form.description"
            placeholder="Provide a detailed description of the incident..."
            rows="4"
            :maxlength="2000"
            :disabled="isLoading"
            class="resize-none"
          />
          <div class="flex justify-between text-xs text-gray-500">
            <div v-if="errors.description" class="text-red-600">
              {{ errors.description }}
            </div>
            <div class="ml-auto">
              {{ form.description.length }}/2000
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <DialogFooter class="flex gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            @click="handleClose"
            :disabled="isLoading"
            class="text-xs"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :disabled="isLoading || !isFormValid"
            class="bg-red-600 hover:bg-red-700 text-white text-xs"
          >
            <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" />
            Submit Report
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { MessageSquareWarning, Loader2, ChevronsUpDown, Check } from 'lucide-vue-next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { useToast } from 'primevue/usetoast'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface User {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string
}

interface FellowReportForm {
  fellow_username: string
  reason: string
  description: string
  evidence_notes: string
  progress?: number
}

const props = defineProps<{
  isOpen: boolean
  progressId?: number
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submitted'): void
}>()

const toast = useToast()

// State
const isLoading = ref(false)
const userList = ref<User[]>([])
const selectedUser = ref<User | null>(null)
const open = ref(false)
const form = ref<FellowReportForm>({
  fellow_username: '',
  reason: '',
  description: '',
  evidence_notes: ''
})

const errors = ref<Partial<Record<keyof FellowReportForm, string>>>({})

// Computed
const filteredUsers = computed(() => {
  return userList.value
})

const isFormValid = computed(() => {
  return (
    form.value.fellow_username.trim() !== '' &&
    form.value.reason.trim() !== '' &&
    form.value.description.trim() !== '' &&
    form.value.description.length <= 2000 &&
    form.value.evidence_notes.length <= 1000 &&
    Object.keys(errors.value).length === 0
  )
})

// Methods
const fetchUserList = async () => {
  try {
    const response = await api.get(API_ROUTES.GET_USER_LIST)
    // Sort users alphabetically by first name, then last name
    userList.value = response.data.sort((a: User, b: User) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase()
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase()
      return nameA.localeCompare(nameB)
    })
  } catch (error) {
    console.error('Error fetching user list:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load user list',
      life: 3000
    })
  }
}

const handleUserSelection = (user: User) => {
  selectedUser.value = user
  form.value.fellow_username = user.username
  open.value = false
}

const validateForm = () => {
  errors.value = {}

  if (!form.value.fellow_username.trim()) {
    errors.value.fellow_username = 'Please select a fellow to report'
  }

  if (!form.value.reason.trim()) {
    errors.value.reason = 'Please select a reason'
  }

  if (!form.value.description.trim()) {
    errors.value.description = 'Description is required'
  } else if (form.value.description.length > 2000) {
    errors.value.description = 'Description must be 2000 characters or less'
  }

  if (form.value.evidence_notes.length > 1000) {
    errors.value.evidence_notes = 'Evidence notes must be 1000 characters or less'
  }
}

const resetForm = () => {
  form.value = {
    fellow_username: '',
    reason: '',
    description: '',
    evidence_notes: ''
  }
  errors.value = {}
  selectedUser.value = null
  open.value = false
}

const handleSubmit = async () => {
  validateForm()

  if (!isFormValid.value) {
    return
  }

  isLoading.value = true

  try {
    const payload = {
      ...form.value,
      ...(props.progressId && { progress: props.progressId })
    }

    await api.post(API_ROUTES.SUBMIT_FELLOW_REPORT, payload)

    toast.add({
      severity: 'success',
      summary: 'Report Submitted',
      detail: 'Your report has been submitted successfully and will be reviewed by our team.',
      life: 5000
    })

    emit('submitted')
    handleClose()
  } catch (error: any) {
    console.error('Error submitting fellow report:', error)
    
    let errorMessage = 'Failed to submit report. Please try again.'
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    }

    toast.add({
      severity: 'error',
      summary: 'Submission Failed',
      detail: errorMessage,
      life: 5000
    })
  } finally {
    isLoading.value = false
  }
}

const handleClose = () => {
  if (!isLoading.value) {
    resetForm()
    emit('close')
  }
}

// Watchers
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUserList()
  }
})

// Lifecycle
onMounted(() => {
  if (props.isOpen) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUserList()
  }
})
</script> 